import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Calendar, RotateCcw, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/hooks/use-language';
import { useIsMobile } from '@/hooks/use-mobile';

interface DateRangeCalendarProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (startDate: Date | null, endDate: Date | null) => void;
  onReset: () => void;
  initialStartDate?: Date | null;
  initialEndDate?: Date | null;
}

const WEEKDAYS_RU = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const WEEKDAYS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAYS_UZ = ['Du', 'Se', 'Ch', 'Pa', 'Ju', 'Sh', 'Ya'];

const MONTHS_RU = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_UZ = ['Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun', 'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr'];

const MIN_YEAR = 2026;
const MAX_YEAR = 2036;

export const DateRangeCalendar: React.FC<DateRangeCalendarProps> = ({
  isOpen,
  onClose,
  onApply,
  onReset,
  initialStartDate = null,
  initialEndDate = null
}) => {
  const { language } = useLanguage();
  const isMobile = useIsMobile();
  
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(Math.max(new Date().getFullYear(), MIN_YEAR));
  const [startDate, setStartDate] = useState<Date | null>(initialStartDate || null);
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate || null);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [showYearSelector, setShowYearSelector] = useState(false);

  // Sync internal state with props when modal opens
  useEffect(() => {
    if (isOpen) {
      setStartDate(initialStartDate || null);
      setEndDate(initialEndDate || null);
      
      // If we have an initial date, jump to that month/year
      const dateToFocus = initialStartDate || initialEndDate || new Date();
      if (dateToFocus.getFullYear() >= MIN_YEAR) {
        setCurrentMonth(dateToFocus.getMonth());
        setCurrentYear(dateToFocus.getFullYear());
      }
    }
  }, [isOpen, initialStartDate, initialEndDate]);

  const weekdays = language === 'ru' ? WEEKDAYS_RU : language === 'uz' ? WEEKDAYS_UZ : WEEKDAYS_EN;
  const months = language === 'ru' ? MONTHS_RU : language === 'uz' ? MONTHS_UZ : MONTHS_EN;

  const labels = useMemo(() => ({
    title: language === 'ru' ? 'Выбор периода' : language === 'uz' ? 'Muddatni tanlash' : 'Select Period',
    reset: language === 'ru' ? 'Сброс' : language === 'uz' ? 'Tozalash' : 'Reset',
    apply: language === 'ru' ? 'Применить' : language === 'uz' ? 'Qo\'llash' : 'Apply',
    rangeLabel: language === 'ru' ? 'Выбранный диапазон:' : language === 'uz' ? 'Tanlangan muddat:' : 'Selected range:',
    notSelected: language === 'ru' ? 'не выбран' : language === 'uz' ? 'tanlanmagan' : 'not selected'
  }), [language]);

  const years = useMemo(() => {
    const arr = [];
    for (let y = MIN_YEAR; y <= MAX_YEAR; y++) {
      arr.push(y);
    }
    return arr;
  }, []);

  const getDaysInMonth = useCallback((month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  }, []);

  const getFirstDayOfMonth = useCallback((month: number, year: number) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; // Convert to Monday-first
  }, []);

  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days: (number | null)[] = [];
    
    // Fill empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    
    // Fill actual days
    for (let d = 1; d <= daysInMonth; d++) {
      days.push(d);
    }
    
    return days;
  }, [currentMonth, currentYear, getDaysInMonth, getFirstDayOfMonth]);

  const handleDayClick = useCallback((day: number) => {
    const clickedDate = new Date(currentYear, currentMonth, day);
    
    if (!startDate || (startDate && endDate)) {
      // Start fresh selection
      setStartDate(clickedDate);
      setEndDate(null);
    } else {
      // We have a start, now set end
      if (clickedDate < startDate) {
        // If clicked before start, reset start to this date
        setStartDate(clickedDate);
        setEndDate(null);
      } else {
        setEndDate(clickedDate);
      }
    }
  }, [startDate, endDate, currentMonth, currentYear]);

  const isInRange = useCallback((day: number) => {
    if (!startDate || !day) return false;
    const current = new Date(currentYear, currentMonth, day);
    
    const effectiveEnd = endDate || hoverDate;
    if (!effectiveEnd) return false;
    
    const start = startDate <= effectiveEnd ? startDate : effectiveEnd;
    const end = startDate <= effectiveEnd ? effectiveEnd : startDate;
    
    return current >= start && current <= end;
  }, [startDate, endDate, hoverDate, currentMonth, currentYear]);

  const isStartOrEnd = useCallback((day: number) => {
    if (!day) return false;
    const current = new Date(currentYear, currentMonth, day);
    
    if (startDate && current.getTime() === startDate.getTime()) return true;
    if (endDate && current.getTime() === endDate.getTime()) return true;
    return false;
  }, [startDate, endDate, currentMonth, currentYear]);

  const prevMonth = useCallback(() => {
    if (currentMonth === 0) {
      if (currentYear > MIN_YEAR) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      }
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  }, [currentMonth, currentYear]);

  const nextMonth = useCallback(() => {
    if (currentMonth === 11) {
      if (currentYear < MAX_YEAR) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      }
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  }, [currentMonth, currentYear]);

  const handleYearSelect = useCallback((year: number) => {
    setCurrentYear(year);
    setShowYearSelector(false);
  }, []);

  const handleReset = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    setHoverDate(null);
    onReset();
  }, [onReset]);

  const handleApply = useCallback(() => {
    // If only one date is selected, use it as both start and end (single day filter)
    const finalStart = startDate;
    const finalEnd = endDate || startDate;
    
    if (finalStart && finalEnd) {
      onApply(finalStart, finalEnd);
      onClose();
    }
  }, [startDate, endDate, onApply, onClose]);

  const formatDateDisplay = (date: Date | null) => {
    if (!date) return '';
    const d = date.getDate().toString().padStart(2, '0');
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const y = date.getFullYear();
    return `${d}.${m}.${y}`;
  };

  const rangeText = startDate && endDate 
    ? `${formatDateDisplay(startDate)} – ${formatDateDisplay(endDate)}`
    : labels.notSelected;

  const canApply = startDate !== null;

  const canGoPrev = currentMonth > 0 || currentYear > MIN_YEAR;
  const canGoNext = currentMonth < 11 || currentYear < MAX_YEAR;

  // Fully block background scroll when open
  useEffect(() => {
    if (isOpen) {
      // Get current scroll position to restore later
      const scrollY = window.scrollY;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      
      // Lock body
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }

      // Block all scroll-related events
      const preventDefault = (e: Event) => {
        // Allow scrolling inside elements with data-scrollable
        if (e.target instanceof HTMLElement && e.target.closest('[data-scrollable="true"]')) {
          return;
        }
        e.preventDefault();
      };

      const handleKeyDown = (e: KeyboardEvent) => {
        const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', ' '];
        if (scrollKeys.includes(e.key)) {
          // Allow keyboard navigation in year selector if focused
          if (e.target instanceof HTMLElement && e.target.closest('[data-scrollable="true"]')) {
            return;
          }
          e.preventDefault();
        }
      };

      window.addEventListener('wheel', preventDefault, { passive: false });
      window.addEventListener('touchmove', preventDefault, { passive: false });
      window.addEventListener('keydown', handleKeyDown, { capture: true });

      return () => {
        // Restore body
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollY);

        window.removeEventListener('wheel', preventDefault);
        window.removeEventListener('touchmove', preventDefault);
        window.removeEventListener('keydown', handleKeyDown, { capture: true });
      };
    }
  }, [isOpen]);

  const calendarContent = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-[#13100B]/85 backdrop-blur-xl"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 10 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "relative bg-background border border-white/10 shadow-[0_0_50px_rgba(0,0,0,0.8)] flex flex-col mirror-style overflow-hidden",
              isMobile 
                ? "w-[92%] h-[70vh] rounded-[18px]" 
                : "w-full max-w-md md:rounded-none"
            )}
          >
            {/* Close Button - matching Portfolio modal style */}
            <button
              onClick={onClose}
              className={cn(
                "absolute top-4 right-4 z-[70] flex items-center justify-center bg-background/50 backdrop-blur-md border border-white/10 text-white transition-all duration-300",
                isMobile ? "w-10 h-10 rounded-full" : "w-10 h-10 hover:bg-primary rounded-sm"
              )}
            >
              <X size={20} />
            </button>

            <div className="flex-1 overflow-y-auto no-scrollbar flex flex-col" data-scrollable="true">
              {/* Header */}
              <div className="flex items-center justify-between p-4 lg:p-6 border-b border-white/10 flex-shrink-0">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_10px_rgba(176,138,90,0.8)]"></div>
                    <div className="text-primary text-[9px] font-black uppercase tracking-[0.3em]">
                      {language === 'ru' ? 'ФИЛЬТР' : language === 'uz' ? 'FILTR' : 'FILTER'}
                    </div>
                  </div>
                  <h3 className="text-lg lg:text-xl font-black uppercase tracking-widest text-white">
                    {labels.title}
                  </h3>
                </div>
              </div>
              
              {/* Calendar Controls & Grid */}
              <div className="p-4 lg:p-6 space-y-6">
                <div className="flex items-center justify-between">
                  {/* Month Navigation */}
                  <div className="flex items-center gap-4">
                    <button
                      onClick={prevMonth}
                      disabled={!canGoPrev}
                      className={cn(
                        "p-2 transition-colors border border-white/5 bg-white/5 rounded-sm",
                        canGoPrev 
                          ? "text-white/50 hover:text-primary hover:border-primary/50" 
                          : "text-white/10 cursor-not-allowed"
                      )}
                    >
                      <ChevronLeft size={18} />
                    </button>
                    
                    <span className="text-xs lg:text-sm font-bold uppercase tracking-widest text-white min-w-[100px] text-center">
                      {months[currentMonth]}
                    </span>
                    
                    <button
                      onClick={nextMonth}
                      disabled={!canGoNext}
                      className={cn(
                        "p-2 transition-colors border border-white/5 bg-white/5 rounded-sm",
                        canGoNext 
                          ? "text-white/50 hover:text-primary hover:border-primary/50" 
                          : "text-white/10 cursor-not-allowed"
                      )}
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>

                  {/* Year Selector */}
                  <div className="relative">
                    <button
                      onClick={() => setShowYearSelector(!showYearSelector)}
                      className="flex items-center gap-2 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-white/70 hover:text-primary border border-white/10 hover:border-primary/50 bg-white/5 transition-all"
                    >
                      {currentYear}
                      <ChevronDown size={14} className={cn("transition-transform duration-300", showYearSelector && "rotate-180")} />
                    </button>
                    
                    <AnimatePresence>
                      {showYearSelector && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full right-0 mt-2 bg-[#1a1611] border border-white/10 max-h-48 overflow-y-auto z-[80] custom-scrollbar shadow-2xl"
                        >
                          {years.map(year => (
                            <button
                              key={year}
                              onClick={() => handleYearSelect(year)}
                              className={cn(
                                "block w-full px-6 py-2.5 text-[10px] font-bold text-left transition-colors uppercase tracking-widest",
                                year === currentYear
                                  ? "bg-primary text-white"
                                  : "text-white/60 hover:text-white hover:bg-white/5"
                              )}
                            >
                              {year}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* Weekdays Header */}
                  <div className="grid grid-cols-7 gap-1">
                    {weekdays.map(day => (
                      <div 
                        key={day} 
                        className="text-center text-[9px] font-black uppercase tracking-widest text-white/20 py-2"
                      >
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  {/* Days Grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {calendarDays.map((day, index) => {
                      if (day === null) {
                        return <div key={`empty-${index}`} className="aspect-square" />;
                      }
                      
                      const inRange = isInRange(day);
                      const isEndpoint = isStartOrEnd(day);
                      
                      return (
                        <button
                          key={`day-${day}`}
                          onClick={() => handleDayClick(day)}
                          onMouseEnter={() => {
                            if (startDate && !endDate) {
                              setHoverDate(new Date(currentYear, currentMonth, day));
                            }
                          }}
                          onMouseLeave={() => setHoverDate(null)}
                          className={cn(
                            "aspect-square flex items-center justify-center text-[10px] font-black transition-all duration-300 relative uppercase tracking-tighter",
                            isEndpoint
                              ? "bg-primary text-white shadow-[0_0_15px_rgba(176,138,90,0.4)] z-10"
                              : inRange
                                ? "bg-primary/20 text-white"
                                : "text-white/40 hover:bg-white/5 hover:text-white border border-transparent hover:border-white/10"
                          )}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Range Info Display in Modal Body */}
                <div className="bg-white/5 border border-white/10 p-4 space-y-2">
                  <div className="text-[9px] font-black uppercase tracking-[0.2em] text-white/20">
                    {labels.rangeLabel}
                  </div>
                  <div className="text-xs font-black text-primary uppercase tracking-wider">
                    {rangeText}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="border-t border-white/10 p-4 lg:p-6 bg-background/80 backdrop-blur-md flex-shrink-0">
              <div className="flex items-center justify-between gap-4">
                {/* Reset Button */}
                <button
                  onClick={handleReset}
                  className="flex items-center gap-2 px-4 py-3 text-[9px] font-black uppercase tracking-[0.2em] text-white/40 hover:text-white border border-white/5 hover:border-white/20 bg-white/5 transition-all"
                >
                  <RotateCcw size={14} />
                  {labels.reset}
                </button>
                
                {/* Apply Button */}
                <button
                  onClick={handleApply}
                  disabled={!canApply}
                  className={cn(
                    "flex-1 py-3 text-[9px] font-black uppercase tracking-[0.3em] transition-all",
                    canApply
                      ? "bg-primary text-white hover:bg-primary/80 shadow-[0_0_20px_rgba(176,138,90,0.3)]"
                      : "bg-white/5 text-white/10 cursor-not-allowed border border-white/5"
                  )}
                >
                  {labels.apply}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  if (typeof document === 'undefined') return null;

  return createPortal(calendarContent, document.body);
};
